import mime from 'mime';

import { getOriginalSizeImage } from '@gem/modules/image-resizer';
import { RemoteStorageTasks } from '@gem/modules/remote-storage';
import { downloadBlob } from '@gem/utils';

import Entities from 'STORE';
import { getMessagePayload } from 'UTILS/message/message';

import { IDownload, IDownloadMessageFile } from './download.types';

function getDownloadableFileName(name: string, mimeType: string): string {
  if (mime.getType(name)) {
    return name;
  }

  const splitFileName = name.split('.');
  const fileExtension = splitFileName.length > 1 ? splitFileName[splitFileName.length - 1] : null;

  if (fileExtension) {
    return name;
  }

  const extension = mimeType ? mime.getExtension(mimeType) : null;
  return `${name}${extension ? `.${extension}` : ''}`;
}

async function download(props: IDownload): Promise<void> {
  const { url, fileName, mimeType, onProgress, onError, signal } = props;

  const { blob, isError } = await RemoteStorageTasks.loadRemoteFile(url, {
    onProgress,
    signal,
  });

  if (isError || !blob) {
    onError?.();
    return;
  }

  downloadBlob(blob, getDownloadableFileName(fileName, mimeType));
}

export const downloadMessageFile = ({
  messageId,
  payloadId,
  groupId,
  startDownloadTs,
}: IDownloadMessageFile): void => {
  const group = Entities.GroupsStore.getGroupById(groupId);

  if (!group) {
    return;
  }

  const messagesStore = group.messagesStore;
  const downloadProgress = messagesStore.downloadProgressPerPayload.get(payloadId);
  const isDownloadProcessAlreadyStarted = Boolean(downloadProgress);
  const loaded = downloadProgress?.progressEvent?.loaded || 0;
  const total = downloadProgress?.progressEvent?.total || 0;
  const message = messagesStore.getMessageById(messageId || '');

  const payload = message ? getMessagePayload(message, payloadId) : undefined;

  const { url, userFileBlobUrl, fileName, mimeType } = payload || {};

  if (
    (!url && !userFileBlobUrl) ||
    (isDownloadProcessAlreadyStarted && !downloadProgress?.hasError && loaded < total)
  ) {
    return;
  }

  const onProgress = (event: ProgressEvent): void => {
    messagesStore.downloadProgressPerPayload.setProgressEvent(payloadId, event);
  };

  const onError = (): void => {
    messagesStore.downloadProgressPerPayload.update(payloadId, { hasError: true });
  };

  const controller = new AbortController();

  download({
    url: userFileBlobUrl || getOriginalSizeImage(url),
    fileName,
    mimeType,
    onProgress,
    onError,
    signal: controller.signal,
  });

  messagesStore.downloadProgressPerPayload.update(payloadId, {
    abort: controller.abort,
    hasError: false,
    messageId,
    groupId,
    payloadId,
    startDownloadTs: startDownloadTs || Date.now(),
    completeDownloadTs: 0,
  });
};

export const removeActiveMessageDownloads = (messageId: string, groupId: string): void => {
  const group = Entities.GroupsStore.getGroupById(groupId);

  if (group) {
    const messagesStore = group.messagesStore;
    const downloads = messagesStore.downloadProgressPerPayload.getDownloadsByMessageId(messageId);

    downloads.forEach((downloadProgress) => {
      if (downloadProgress) {
        downloadProgress.abort?.();
        messagesStore.downloadProgressPerPayload.remove(messageId);
      }
    });
  }
};
